/**
 * Batch and Clear commands for complex operations
 */

import { BaseCommand } from './BaseCommand';
import { MapCommandType, IMapCommand, IClearCommandData } from '../types';
import { MarkerManager } from '../../managers/MarkerManager';
import { LineManager } from '../../managers/LineManager';
import { PolygonManager } from '../../managers/PolygonManager';

export class BatchCommand extends BaseCommand {
  private commands: IMapCommand[];

  constructor(commands: IMapCommand[], description?: string) {
    super(MapCommandType.BATCH, description || `Batch operation (${commands.length} commands)`);
    this.commands = commands;
  }

  execute(): void {
    for (const command of this.commands) {
      if (command.canExecute()) {
        command.execute();
      }
    }
  }

  undo(): void {
    for (let i = this.commands.length - 1; i >= 0; i--) {
      const command = this.commands[i];
      if (command.canUndo()) {
        command.undo();
      }
    }
  }

  canExecute(): boolean {
    return this.commands.some(cmd => cmd.canExecute());
  }

  canUndo(): boolean {
    return this.commands.some(cmd => cmd.canUndo());
  }
}

/**
 * For atomic operations that involve multiple related actions
 */
export class CompositeCommand extends BaseCommand {
  private commands: IMapCommand[];
  private isAtomic: boolean;

  constructor(commands: IMapCommand[], description: string, isAtomic: boolean = true) {
    super(MapCommandType.BATCH, description);
    this.commands = commands;
    this.isAtomic = isAtomic;
  }

  execute(): void {
    if (this.isAtomic) {
      const canExecuteAll = this.commands.every(cmd => cmd.canExecute());
      if (!canExecuteAll) {
        console.warn('Cannot execute atomic operation - some commands cannot be executed');
        return;
      }
    }

    for (const command of this.commands) {
      if (command.canExecute()) {
        command.execute();
      }
    }
  }

  undo(): void {
    for (let i = this.commands.length - 1; i >= 0; i--) {
      const command = this.commands[i];
      if (command.canUndo()) {
        command.undo();
      }
    }
  }

  canExecute(): boolean {
    if (this.isAtomic) {
      return this.commands.every(cmd => cmd.canExecute());
    }
    return this.commands.some(cmd => cmd.canExecute());
  }

  canUndo(): boolean {
    if (this.isAtomic) {
      return this.commands.every(cmd => cmd.canUndo());
    }
    return this.commands.some(cmd => cmd.canUndo());
  }

  getCommands(): IMapCommand[] {
    return [...this.commands];
  }
}

export class ClearAllCommand extends BaseCommand {
  private markerManager: MarkerManager;
  private lineManager: LineManager;
  private polygonManager: PolygonManager;
  private previousState: IClearCommandData['previousState'];

  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    polygonManager: PolygonManager
  ) {
    super(MapCommandType.CLEAR_ALL, 'Clear all features');
    this.markerManager = markerManager;
    this.lineManager = lineManager;
    this.polygonManager = polygonManager;

    this.previousState = {
      markers: [...markerManager.getMarkers()],
      lines: [...lineManager.getLines()],
      polygons: [...polygonManager.getPolygons()],
    };
  }

  execute(): void {
    this.markerManager.clearMarkers();
    this.lineManager.clearLines();
    this.polygonManager.clearPolygons();
  }

  undo(): void {
    this.previousState.markers.forEach(marker => {
      this.markerManager.addMarker(marker);
    });

    this.previousState.lines.forEach(line => {
      this.lineManager.addLine(line);
    });

    this.previousState.polygons.forEach(polygon => {
      this.polygonManager.addPolygon(polygon);
    });
  }

  canExecute(): boolean {
    return (
      this.markerManager.getMarkers().length > 0 ||
      this.lineManager.getLines().length > 0 ||
      this.polygonManager.getPolygons().length > 0
    );
  }

  canUndo(): boolean {
    return (
      this.previousState.markers.length > 0 ||
      this.previousState.lines.length > 0 ||
      this.previousState.polygons.length > 0
    );
  }
}
